import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Star, BadgeCheck, MessageCircle, Phone, Mail, Globe, Instagram, Linkedin, Facebook, Youtube, Award, Users, Calendar, ChevronRight, PlayCircle, Heart, MessageSquare, Share2, ArrowLeft, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';
import { CATEGORIES } from '../mock/mock';
import LawFirmCard from '../components/LawFirmCard';
import SEO from '../components/SEO';
import ReviewsSection from '../components/ReviewsSection';
import RelatedReading from '../components/RelatedReading';
import { firmsApi } from '../api/client';
import FirmsMap from '../components/FirmsMap';
import { CITIES } from '../mock/mock';
import { ReceptionistChat } from '../components/ReceptionistChat';
import TeamSection from '../components/TeamSection';

const SOCIAL_BASE = { instagram: 'https://instagram.com/', linkedin: 'https://linkedin.com/in/', facebook: 'https://facebook.com/', youtube: 'https://youtube.com/@' };
const socialUrl = (key, value) => {
  if (!value) return null;
  if (/^https?:\/\//.test(value)) return value;
  return SOCIAL_BASE[key] + value.replace(/^@/, '');
};

export default function LawFirmDetail() {
  const { slug } = useParams();
  const [firm, setFirm] = useState(null);
  const [related, setRelated] = useState([]);
  const [openVideo, setOpenVideo] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setFirm(null); setNotFound(false);
    firmsApi.get(slug)
      .then(f => {
        setFirm(f);
        firmsApi.list({ city: f.city, limit: 5 }).then(d => setRelated(d.items.filter(x => x.id !== f.id).slice(0, 4)));
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="bg-white border border-[#cbd8dc] rounded-[10px] p-10 text-center max-w-md" data-testid="firm-unavailable">
        <MapPin className="w-10 h-10 text-[#6b858e] mx-auto mb-3" />
        <h1 className="text-xl font-semibold text-[#153e44]">Perfil indisponível</h1>
        <p className="text-sm text-[#425a62] mt-2">Este perfil não existe ou está temporariamente suspenso para análise.</p>
        <Link to="/advogados"><Button className="mt-5 bg-[#153e44] hover:bg-[#0c3036]">Ver outros escritórios</Button></Link>
      </div>
    </div>
  );
  if (!firm) return <div className="min-h-[60vh] flex items-center justify-center text-[#52676e]">Carregando...</div>;

  const whatsappUrl = `https://wa.me/${firm.whatsapp}?text=${encodeURIComponent('Olá! Vi seu perfil no Ambito Advogados e gostaria de uma consulta.')}`;
  const categoryNames = firm.categories.map(s => CATEGORIES.find(c => c.slug === s)?.name).filter(Boolean);
  const socialLinks = Object.entries(firm.social || {}).map(([key, v]) => ({ key, url: socialUrl(key, v) })).filter(s => s.url);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const absImg = (src) => (src && src.startsWith('/') ? origin + src : src);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'LegalService',
    name: firm.name,
    description: firm.bio,
    image: absImg(firm.photo),
    telephone: firm.phone,
    ...(firm.email ? { email: firm.email } : {}),
    url: typeof window !== 'undefined' ? window.location.href : '',
    ...(firm.website ? { sameAs: [firm.website, ...socialLinks.map(s => s.url)] } : {}),
    ...(firm.team?.length ? { employee: firm.team.map(m => ({ '@type': 'Person', name: m.name, jobTitle: m.role, ...(m.instagram ? { sameAs: [m.instagram] } : {}) })) } : {}),
    address: { '@type': 'PostalAddress', addressLocality: firm.cityName, addressRegion: firm.state, addressCountry: 'BR', streetAddress: firm.address },
    areaServed: firm.cityName,
    ...(firm.rating > 0 && firm.reviews > 0 ? {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: firm.rating,
        reviewCount: firm.reviews,
        bestRating: 5, worstRating: 1,
      },
    } : {}),
  };

  return (
    <div>
      <SEO
        noIndex={firm.seoQuality ? !firm.seoQuality.indexable : false}
        title={`${firm.name} - ${firm.cityName}/${firm.state}`}
        description={`${firm.bio} Contate ${firm.name} via WhatsApp. ${firm.reviews} avaliações, nota ${firm.rating.toFixed(1)}/5.`}
        image={absImg(firm.photo)}
        type="profile"
        jsonLd={jsonLd}
      />

      <div className="relative h-56 md:h-72 bg-[#f2f6f7] border-b border-[#cbd8dc] overflow-hidden">
        <img src={firm.cover} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#122f36]/70 to-transparent" />
        <div className="absolute top-4 left-4">
          <Link to="/advogados"><Button variant="outline" size="sm" className="bg-white border-[#2f5d65] text-[#153e44] hover:bg-[#edf4f5]"><ArrowLeft className="w-4 h-4 mr-1.5" /> Voltar</Button></Link>
        </div>
      </div>

      <div className="aj-wrap -mt-24 relative">
        <div className="bg-white rounded-[10px] border border-[#cbd8dc] p-6 md:p-8">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="relative shrink-0">
              <img src={firm.photo} alt={firm.leadLawyer} className="w-32 h-32 md:w-40 md:h-40 rounded-[10px] object-cover border-4 border-white" />
              {firm.verified && <div className="absolute -bottom-2 -right-2 bg-white rounded-full p-1 shadow"><BadgeCheck className="w-7 h-7 text-[#0a7d3e]" /></div>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                {firm.sponsored && <Badge className="bg-[#153e44] hover:bg-[#153e44] text-white border-0" data-testid="sponsored-badge">Patrocinado</Badge>}
                {firm.featured && <Badge className="bg-[#153e44] hover:bg-[#153e44] text-white border-0"><Award className="w-3 h-3 mr-1" /> Destaque</Badge>}
                {firm.verified && <Badge variant="outline" className="border-[#0a7d3e] text-[#0a7d3e]"><BadgeCheck className="w-3 h-3 mr-1" /> Verificado OAB</Badge>}
              </div>
              <h1 className="text-2xl md:text-3xl font-semibold text-[#153e44]">{firm.name}</h1>
              <p className="text-[#425a62] mt-1">{[firm.leadLawyer, firm.oab].filter(Boolean).join(' • ')}</p>
              <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-[#425a62]">
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-[#075a6d]" /> {firm.cityName}/{firm.state}</span>
                {firm.yearsExp > 0 && <span className="flex items-center gap-1"><Calendar className="w-4 h-4 text-[#075a6d]" /> {firm.yearsExp} anos de experiência</span>}
                <span className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-[#153e44] text-[#153e44]" />
                  {firm.reviews > 0 ? (<>
                    <span className="font-semibold text-[#153e44]">{firm.rating.toFixed(1)}</span>
                    <span className="text-[#52676e]">({firm.reviews} avaliações)</span>
                  </>) : <span className="text-[#52676e]" data-testid="firm-no-reviews">Sem avaliações ainda</span>}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-4">
                {categoryNames.map(name => <span key={name} className="text-xs font-medium bg-[#f2f6f7] text-[#354c54] px-2.5 py-1 rounded-full">{name}</span>)}
              </div>
              <div className="flex flex-wrap gap-2 mt-6">
                {['crescimento', 'autoridade'].includes(firm.plan) && <ReceptionistChat firm={firm} />}
                {firm.whatsapp && <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"><Button className="bg-[#25D366] hover:bg-[#1fb958] text-white h-11"><MessageCircle className="w-4 h-4 mr-2" /> Falar no WhatsApp</Button></a>}
                {firm.phone && <a href={`tel:${firm.phone}`}><Button variant="outline" className="h-11"><Phone className="w-4 h-4 mr-2" /> Ligar</Button></a>}
                {firm.email && <a href={`mailto:${firm.email}`}><Button variant="outline" className="h-11"><Mail className="w-4 h-4 mr-2" /> E-mail</Button></a>}
                <Button variant="outline" className="h-11" onClick={() => { navigator.clipboard.writeText(window.location.href); }}><Share2 className="w-4 h-4 mr-2" /> Compartilhar</Button>
              </div>
            </div>
          </div>
        </div>

        {firm.claimStatus === 'nao_reivindicado' && (
          <div className="mt-4 aj-edition bg-[#f7fafb] rounded-r-[10px] p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="claim-banner">
            <div className="text-[#122f36]">
              <div className="font-semibold flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-[#075a6d]" /> Este perfil é seu?</div>
              <p className="text-sm text-[#425a62] mt-0.5">Reivindique gratuitamente para editar os dados, receber contatos e acessar o painel do escritório.</p>
            </div>
            <Link to={`/reivindicar/${firm.slug}`} className="shrink-0">
              <Button className="bg-[#153e44] hover:bg-[#0c3036] text-white font-semibold" data-testid="claim-banner-btn">Reivindicar gratuitamente</Button>
            </Link>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 mt-6">
          <div className="min-w-0">
            <Tabs defaultValue="sobre" className="w-full">
              <TabsList className="bg-white border border-[#cbd8dc] h-auto p-1 flex-wrap justify-start w-full">
                <TabsTrigger value="sobre">Sobre</TabsTrigger>
                <TabsTrigger value="avaliacoes">Avaliações ({firm.reviews})</TabsTrigger>
                <TabsTrigger value="artigos">Artigos ({firm.articles?.length || 0})</TabsTrigger>
                <TabsTrigger value="videos">Vídeos ({firm.videos?.length || 0})</TabsTrigger>
                <TabsTrigger value="posts">Posts</TabsTrigger>
              </TabsList>

              <TabsContent value="sobre" className="space-y-6 mt-6">
                <section className="bg-white rounded-[10px] border border-[#cbd8dc] p-6"><h2 className="text-lg font-semibold text-[#153e44] mb-3">Bio</h2><p className="text-[#354c54] leading-relaxed">{firm.bio}</p></section>
                {firm.description && <section className="bg-white rounded-[10px] border border-[#cbd8dc] p-6"><h2 className="text-lg font-semibold text-[#153e44] mb-3">Sobre o escritório</h2><p className="text-[#354c54] leading-relaxed whitespace-pre-line">{firm.description}</p></section>}
                <TeamSection team={firm.team} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {firm.whoWeServe?.length > 0 && (
                    <section className="bg-white rounded-[10px] border border-[#cbd8dc] p-6">
                      <h2 className="text-lg font-semibold text-[#153e44] mb-4 flex items-center gap-2"><Users className="w-5 h-5 text-[#075a6d]" /> Quem atendemos</h2>
                      <ul className="space-y-2">{firm.whoWeServe.map((who, i) => <li key={i} className="flex items-start gap-2 text-[#354c54]"><div className="w-1.5 h-1.5 rounded-full bg-[#153e44] mt-2 shrink-0" /><span>{who}</span></li>)}</ul>
                    </section>
                  )}
                  {firm.differentials?.length > 0 && (
                    <section className="bg-white rounded-[10px] border border-[#cbd8dc] p-6">
                      <h2 className="text-lg font-semibold text-[#153e44] mb-4 flex items-center gap-2"><BadgeCheck className="w-5 h-5 text-[#075a6d]" /> Diferenciais</h2>
                      <ul className="space-y-2">{firm.differentials.map((d, i) => <li key={i} className="flex items-start gap-2 text-[#354c54]"><div className="w-1.5 h-1.5 rounded-full bg-[#153e44] mt-2 shrink-0" /><span>{d}</span></li>)}</ul>
                    </section>
                  )}
                </div>
                {firm.awards?.length > 0 && (
                  <section className="bg-white rounded-[10px] border border-[#cbd8dc] p-6">
                    <h2 className="text-lg font-semibold text-[#153e44] mb-4 flex items-center gap-2"><Award className="w-5 h-5 text-[#075a6d]" /> Prêmios e reconhecimentos</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {firm.awards.map((a, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 bg-[#f7fafb] rounded-[7px]">
                          <div className="w-9 h-9 rounded-[7px] bg-[#edf4f5] flex items-center justify-center shrink-0"><Award className="w-4 h-4 text-[#075a6d]" /></div>
                          <span className="text-sm font-medium text-[#354c54]">{a}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </TabsContent>

              <TabsContent value="avaliacoes" className="mt-6">
                <ReviewsSection firm={firm} initialReviews={firm.reviewsList || []} onNewReview={() => firmsApi.get(slug).then(setFirm)} />
              </TabsContent>

              <TabsContent value="artigos" className="mt-6">
                {firm.articles?.length === 0 ? (
                  <div className="bg-white rounded-[10px] border border-[#cbd8dc] p-10 text-center text-[#52676e]">Nenhum artigo publicado ainda.</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {firm.articles.map(art => {
                      const Wrapper = art.externalUrl ? 'a' : Link;
                      const wrapProps = art.externalUrl ? { href: art.externalUrl, target: '_blank', rel: 'noopener noreferrer' } : { to: `/artigo/${firm.slug}/${art.id}` };
                      return (
                      <Wrapper key={art.id} {...wrapProps} className="block bg-white rounded-[10px] border border-[#cbd8dc] overflow-hidden hover:border-[#547a83] transition-shadow group" data-testid={`firm-article-${art.id}`}>
                        {art.cover && <div className="aspect-[16/9] overflow-hidden bg-[#f2f6f7]"><img src={art.cover} alt={art.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>}
                        <div className="p-5">
                          <div className="flex items-center gap-2 text-xs text-[#52676e] mb-2"><span>{art.date}</span><span>•</span><span>{art.readTime}</span>{art.author && <><span>•</span><span>Por {art.author}</span></>}</div>
                          <h3 className="font-semibold text-[#153e44] leading-snug group-hover:text-[#075a6d] transition-colors">{art.title}</h3>
                          <p className="text-sm text-[#425a62] mt-2 line-clamp-2">{art.excerpt}</p>
                          {art.externalUrl && <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#153e44] mt-3"><ExternalLink className="w-3.5 h-3.5" /> Ler no site do escritório</span>}
                        </div>
                      </Wrapper>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="videos" className="mt-6">
                {firm.videos?.length === 0 ? (
                  <div className="bg-white rounded-[10px] border border-[#cbd8dc] p-10 text-center text-[#52676e]">Nenhum vídeo publicado.</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {firm.videos.map(v => (
                      <Dialog key={v.id} open={openVideo === v.id} onOpenChange={(o) => setOpenVideo(o ? v.id : null)}>
                        <DialogTrigger asChild>
                          <button className="bg-white rounded-[10px] border border-[#cbd8dc] overflow-hidden hover:border-[#547a83] transition-shadow group text-left">
                            <div className="aspect-video relative overflow-hidden bg-[#122f36]">
                              <img src={`https://img.youtube.com/vi/${v.youtubeId}/hqdefault.jpg`} alt={v.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                              <div className="absolute inset-0 flex items-center justify-center"><div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform"><PlayCircle className="w-10 h-10 text-[#153e44]" /></div></div>
                              <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">{v.duration}</div>
                            </div>
                            <div className="p-4"><h3 className="font-semibold text-[#153e44]">{v.title}</h3></div>
                          </button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-black border-0">
                          <div className="aspect-video"><iframe src={`https://www.youtube.com/embed/${v.youtubeId}?autoplay=1`} title={v.title} className="w-full h-full" allow="accelerometer; autoplay; encrypted-media" allowFullScreen /></div>
                        </DialogContent>
                      </Dialog>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="posts" className="mt-6 space-y-4">
                {firm.posts?.length === 0 ? (
                  <div className="bg-white rounded-[10px] border border-[#cbd8dc] p-10 text-center text-[#52676e]">Nenhum post ainda.</div>
                ) : firm.posts.map(post => (
                  <article key={post.id} className="bg-white rounded-[10px] border border-[#cbd8dc] p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <img src={firm.photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                      <div><div className="font-semibold text-[#153e44] text-sm">{firm.name}</div><div className="text-xs text-[#52676e]">{post.date}</div></div>
                    </div>
                    <p className="text-[#354c54] leading-relaxed">{post.content}</p>
                    <div className="flex items-center gap-4 mt-4 pt-4 border-t border-[#e1e9eb] text-sm text-[#425a62]">
                      <button className="flex items-center gap-1.5 hover:text-[#063f4c]"><Heart className="w-4 h-4" /> {post.likes}</button>
                      <button className="flex items-center gap-1.5 hover:text-[#063f4c]"><MessageSquare className="w-4 h-4" /> Comentar</button>
                      <button className="flex items-center gap-1.5 hover:text-[#063f4c] ml-auto"><Share2 className="w-4 h-4" /> Compartilhar</button>
                    </div>
                  </article>
                ))}
              </TabsContent>
            </Tabs>
          </div>

          <aside className="space-y-4">
            <RelatedReading categories={firm.categories || []} />
            <div className="bg-white rounded-[10px] border border-[#cbd8dc] overflow-hidden">
              <div className="p-3 border-b border-[#e1e9eb]">
                <h3 className="font-semibold text-[#153e44] text-sm">Localização</h3>
              </div>
              {(() => {
                const cityData = CITIES.find(c => c.slug === firm.city);
                const lat = firm.lat ?? cityData?.lat;
                const lng = firm.lng ?? cityData?.lng;
                if (lat == null) return null;
                return <FirmsMap singleFirm={{ ...firm, lat, lng }} firms={[]} height={220} />;
              })()}
            </div>

            <div className="bg-white rounded-[10px] border border-[#cbd8dc] p-5 sticky top-24">
              <h3 className="font-semibold text-[#153e44] mb-4">Contato</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3"><MapPin className="w-4 h-4 text-[#075a6d] mt-0.5 shrink-0" /><span className="text-[#354c54]">{firm.address}</span></div>
                {firm.phone && <div className="flex items-center gap-3"><Phone className="w-4 h-4 text-[#075a6d] shrink-0" /><a href={`tel:${firm.phone}`} className="text-[#354c54] hover:text-[#153e44]">{firm.phone}</a></div>}
                {firm.phoneSecondary && <div className="flex items-center gap-3" data-testid="firm-phone-secondary"><Phone className="w-4 h-4 text-[#075a6d] shrink-0" /><a href={`tel:${firm.phoneSecondary}`} className="text-[#354c54] hover:text-[#153e44]">{firm.phoneSecondary}</a></div>}
                {firm.email && <div className="flex items-center gap-3"><Mail className="w-4 h-4 text-[#075a6d] shrink-0" /><a href={`mailto:${firm.email}`} className="text-[#354c54] hover:text-[#153e44] break-all">{firm.email}</a></div>}
                {firm.website && <div className="flex items-center gap-3"><Globe className="w-4 h-4 text-[#075a6d] shrink-0" /><a href={firm.website} target="_blank" rel="noopener noreferrer" className="text-[#354c54] hover:text-[#153e44] break-all">{firm.website.replace('https://', '')}</a></div>}
              </div>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="block mt-4"><Button className="w-full bg-[#25D366] hover:bg-[#1fb958] text-white"><MessageCircle className="w-4 h-4 mr-2" /> WhatsApp</Button></a>
              {socialLinks.length > 0 && (
              <div className="mt-5 pt-4 border-t border-[#e1e9eb]">
                <h4 className="text-xs font-semibold text-[#52676e] uppercase tracking-wider mb-3">Redes sociais</h4>
                <div className="flex gap-2">
                  {socialLinks.map(({ key, url }) => {
                    const Icon = { instagram: Instagram, linkedin: Linkedin, facebook: Facebook, youtube: Youtube }[key];
                    return <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={key} data-testid={`firm-social-${key}`} className="w-9 h-9 rounded-[7px] bg-[#f2f6f7] hover:bg-[#153e44] hover:text-[#063f4c] text-[#153e44] flex items-center justify-center transition-colors"><Icon className="w-4 h-4" /></a>;
                  })}
                </div>
              </div>
              )}
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-16">
            <h2 className="text-2xl font-semibold text-[#153e44] mb-6">Outros escritórios em {firm.cityName}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">{related.map(f => <LawFirmCard key={f.id} firm={f} />)}</div>
          </section>
        )}

        <div className="h-16" />
      </div>
    </div>
  );
}
